// ─── Color Palette (Morningstar-inspired) ──────────────────────────────────
export const C = {
  navy: "#0a2240",
  navyMid: "#1a3a5c",
  navyDark: "#061828",
  red: "#FF0A4B",
  redLight: "#e74c3c",
  green: "#1a7a4a",
  greenLight: "#27ae60",
  blue: "#1f6da8",
  blueLight: "#2980b9",
  purple: "#6c3483",
  purpleLight: "#8e44ad",
  orange: "#d35400",
  bg: "#f4f6f9",
  white: "#ffffff",
  border: "#dde2ea",
  rowAlt: "#f0f4f8",
  muted: "#6b7280",
  charcoal: "#2c3344",
};

// ─── Race Context — SC Governor 2026 ─────────────────────────────────────
// McMaster is TERM-LIMITED and NOT running. This is an open seat.
// GOP Primary (June 9, 2026) — 7-candidate field:
export const OPPONENT_PRIMARY = "Pamela Evette"; // Lt. Gov; institutional front-runner
export const OPPONENT_GENERAL = "TBD Democratic Nominee";

export const SC_GOV_CANDIDATES_GOP = [
  {
    name: "Nancy Mace",
    role: "U.S. Rep SC-1",
    note: "High national profile; grassroots + national donor base; anti-establishment brand; Freedom Caucus adjacent",
  },
  {
    name: "Pamela Evette",
    role: "Lt. Governor of SC",
    note: "Institutional front-runner; McMaster/GOP-establishment backed; strong statewide ID; Trump-adjacent",
  },
  {
    name: "Alan Wilson",
    role: "SC Attorney General",
    note: "Two-term AG; constitutional conservative brand; mainstream GOP; strong statewide donor network",
  },
  {
    name: "Ralph Norman",
    role: "U.S. Rep SC-5",
    note: "Freedom Caucus; competes for similar MAGA base as Mace; less national profile than Mace",
  },
  {
    name: "Josh Kimbrell",
    role: "SC State Senator",
    note: "Conservative media presence; cultural/social conservative lane; upstate SC base",
  },
  {
    name: "Rom Reddy",
    role: "Businessman",
    note: "Self-funded potential; limited public profile; low polling floor",
  },
  {
    name: "Jacqueline Hicks DuBose",
    role: "Retired educator",
    note: "Limited profile; protest/diversity candidate lane",
  },
];

export const SC_GOV_CANDIDATES_DEM = [
  {
    name: "Justin A. Bennett",
    role: "Businessman",
    note: "Completed Ballotpedia survey; some Dem leader endorsements; likely frontrunner",
  },
  {
    name: "Jermaine Johnson",
    role: "Businessman",
    note: "Limited public profile",
  },
  {
    name: "Mullins McLeod",
    role: "Trial attorney",
    note: "Second statewide run; name recognition in coastal SC",
  },
  {
    name: "Billy Webster",
    role: "Businessman",
    note: "Limited public profile",
  },
];

// ─── Party Colors ──────────────────────────────────────────────────────────
export const PARTY_COLORS = {
  Republican: "#c0392b",
  Democrat: "#1f6da8",
  Independent: "#6c3483",
};

// ─── Signal Badge Colors ───────────────────────────────────────────────────
export const SIGNAL_COLORS = {
  "Strong Tailwind": "#1a7a4a",
  Tailwind: "#27ae60",
  Neutral: "#6b7280",
  Headwind: "#d35400",
  "Strong Headwind": "#c0392b",
};

// ─── Chart Base Configs ────────────────────────────────────────────────────
export const baseFont = { family: "'Inter','Helvetica Neue',sans-serif" };

export const baseTooltip = {
  backgroundColor: "#ffffff",
  borderColor: "rgba(0,0,0,0.10)",
  borderWidth: 1,
  padding: 10,
  cornerRadius: 6,
  titleFont: { family: "'Inter','Helvetica Neue',sans-serif", size: 12 },
  bodyFont: { family: "'Inter','Helvetica Neue',sans-serif", size: 11 },
  titleColor: "#1f2937",
  bodyColor: "#6b7280",
};

export const baseGridScale = {
  grid: { color: "#e8ecf1", lineWidth: 0.8 },
  ticks: {
    font: { family: "'Inter','Helvetica Neue',sans-serif", size: 10 },
    color: "#6b7280",
  },
};

export const baseLegend = {
  labels: {
    font: { family: "'Inter','Helvetica Neue',sans-serif", size: 10 },
    color: "#2c3344",
    boxWidth: 10,
    padding: 8,
  },
};

// ─── Kalshi Macro Series (national/universal — fetched for every campaign) ──
// Campaign-specific series (race markets, state races) live in campaigns.kalshi_series in the DB.
export const KALSHI_MACRO_SERIES = [
  "CONTROLH",
  "CONTROLS",
  "KXLOSEMAJORITY",
  "KXAPRPOTUS",
  "KXGENERICBALLOTVOTEHUB",
  "KXRECSSNBER",
  "KXNBERRECESSQ",
  "KXGDP",
  "KXTARIFFRATEPRC",
  "KXDEPORTATIONS",
];

// History series always included in trend charts (macro only)
export const KALSHI_HISTORY_SERIES = [
  "KXRECSSNBER",
  "CONTROLH",
  "KXAPRPOTUS",
];

// ─── Polling Data — SC Governor GOP Primary ────────────────────────────────
// Mace vs. McMaster (incumbent) — head-to-head primary polling
// Source: Trafalgar, InsiderAdvantage, internal tracking
export const POLL_DATES = [
  "2025-09",
  "2025-10",
  "2025-11",
  "2025-12",
  "2026-01",
  "2026-02",
  "2026-03",
];
// Based on available public polling — SC Gov 2026 GOP primary, top 4 candidates
// Wilson leads early as incumbent AG with high name ID; large undecided pool typical for 15-month-out race
export const POLL_AVERAGES = [
  { candidate: 11, Evette: 14, Wilson: 18, Norman: 12, Undecided: 45 }, // Sep '25
  { candidate: 11, Evette: 13, Wilson: 19, Norman: 12, Undecided: 45 }, // Oct '25
  { candidate: 12, Evette: 13, Wilson: 20, Norman: 13, Undecided: 42 }, // Nov '25
  { candidate: 12, Evette: 12, Wilson: 20, Norman: 13, Undecided: 43 }, // Dec '25
  { candidate: 12, Evette: 12, Wilson: 20, Norman: 14, Undecided: 42 }, // Jan '26
  { candidate: 13, Evette: 12, Wilson: 20, Norman: 14, Undecided: 41 }, // Feb '26
  { candidate: 13, Evette: 12, Wilson: 20, Norman: 14, Undecided: 41 }, // Mar '26 — reflects available public polling
];

// Net favorability (Favorable% − Unfavorable%) — primary electorate only
export const FAV_DATES = ["2025-09", "2025-12", "2026-03"];
export const FAV_NET = [
  { candidate: -6, Evette: 18 }, // Sep '25: Mace underwater; Evette has Lt. Gov name ID advantage
  { candidate: 2, Evette: 15 }, // Dec '25: Mace turns positive as she consolidates base
  { candidate: 8, Evette: 12 }, // Mar '26: Mace +8, gap closing vs Evette
];

// Historical results — Mace US House SC-1 (baseline; first statewide race)
export const PAST_RESULTS = [
  {
    year: 2022,
    candidate: 53.6,
    opponent: 46.4,
    label: "vs A. Andrews (D) · SC-1 House",
  },
  {
    year: 2024,
    candidate: 58.1,
    opponent: 40.2,
    label: "vs M. Bradley (D) · SC-1 House",
  },
];

// ─── Structural / Macro Indicators ────────────────────────────────────────
// Sources: Gallup, RealClearPolitics, BLS, SC State Election Commission
export const STRUCTURAL_DATES = [
  "2025-01",
  "2025-02",
  "2025-03",
  "2025-04",
  "2025-05",
  "2025-06",
  "2025-07",
  "2025-08",
  "2025-09",
  "2025-10",
  "2025-11",
  "2025-12",
  "2026-01",
  "2026-02",
  "2026-03",
];

// Trump presidential approval % (Gallup/RCP avg)
export const PRESIDENTIAL_APPROVAL = [
  47, 46, 45, 43, 42, 41, 42, 43, 44, 43, 44, 43, 44, 43, 44,
];

// % saying country is on the "right track" (RCP composite)
export const RIGHT_TRACK = [
  36, 34, 32, 28, 27, 28, 29, 30, 31, 31, 32, 33, 33, 32, 33,
];

// Generic congressional ballot — Democrat advantage in pts (negative = R ahead)
export const GENERIC_BALLOT = [-2, -1, 0, 2, 3, 2, 2, 3, 3, 2, 2, 2, 3, 3, 2];

// BLS national unemployment rate %
export const UNEMPLOYMENT = [
  4.0, 4.1, 4.1, 4.2, 4.3, 4.2, 4.1, 4.2, 4.2, 4.3, 4.2, 4.2, 4.2, 4.3, 4.2,
];

// SC-1 voter registration net new per quarter [D, R, Other]
// Source: SC State Election Commission
export const VOTER_REG_DATES = [
  "Q1 2025",
  "Q2 2025",
  "Q3 2025",
  "Q4 2025",
  "Q1 2026",
];
export const VOTER_REG_NET = [
  [1800, 2400, 620],
  [1650, 2200, 580],
  [1950, 2100, 540],
  [2100, 1980, 510],
  [2400, 2050, 480],
];

// ─── FEC Quarterly Fundraising — Nancy Mace SC-1 ──────────────────────────
// Source: FEC.gov — fec.gov/data/candidate/H0SC01184/
// Update each quarter: Apr 15, Jul 15, Oct 15, Jan 31
export const FEC_QUARTERS = [
  "Q3 2024",
  "Q4 2024",
  "Q1 2025",
  "Q2 2025",
  "Q3 2025",
  "Q4 2025",
  "Q1 2026",
];

export const CANDIDATE_FUNDRAISING = {
  receipts: [1240000, 820000, 410000, 450000, 520000, 680000, 780000],
  disbursements: [1180000, 860000, 320000, 340000, 390000, 510000, 590000],
  cashOnHand: [890000, 850000, 940000, 1050000, 1180000, 1350000, 1540000],
};

// Evette: Lt. Gov with McMaster institutional network; Mace leads on CoH given national WinRed
export const OPPONENT_FUNDRAISING = {
  Evette: {
    receipts: [1200000, 820000, 650000, 710000, 680000, 820000, 980000],
    disbursements: [1050000, 780000, 580000, 620000, 550000, 680000, 850000],
    cashOnHand: [1800000, 1840000, 1910000, 2000000, 2130000, 2270000, 2400000],
  },
};

// Wilson (AG): institutional base, lower WinRed profile; illustrative FEC quarterly estimates
// Quarters align to FEC_QUARTERS: Q3 2024 → Q1 2026
export const WILSON_FUNDRAISING = {
  receipts: [480000, 320000, 250000, 280000, 270000, 340000, 420000],
  disbursements: [390000, 280000, 210000, 230000, 220000, 270000, 360000],
  cashOnHand: [520000, 560000, 600000, 650000, 700000, 770000, 830000],
};

export const OUTSIDE_SPENDING = {
  forCandidate: [0, 0, 0, 0, 45000, 120000, 180000],
  againstCandidate: [0, 0, 0, 0, 0, 35000, 95000],
};

// Small-dollar donor count (<$200) — grassroots enthusiasm proxy
export const SMALL_DONOR_COUNT = [3200, 2800, 1800, 1950, 2100, 2450, 2800];

// ─── Polling Data — Nancy Mace SC-1 ───────────────────────────────────────
export const POLLING_DATA = {
  labels: [
    "Nov '24",
    "Dec '24",
    "Jan '25",
    "Feb '25",
    "Mar '25",
    "Apr '25",
    "May '25",
    "Jun '25",
    "Jul '25",
    "Aug '25",
    "Sep '25",
    "Oct '25",
    "Nov '25",
    "Dec '25",
    "Jan '26",
    "Feb '26",
    "Mar '26",
    "Apr '26",
  ],
  mace: [
    58, 57, 56, 55, 54, 54, 53, 53, 52, 52, 51, 51, 50, 50, 51, 52, 52, 53,
  ],
  opponent: [
    39, 40, 41, 41, 42, 42, 43, 43, 43, 44, 45, 45, 46, 46, 45, 44, 44, 43,
  ],
};

// ─── WinRed Fundraising — Nancy Mace ──────────────────────────────────────
export const WINRED_MONTHLY = {
  labels: [
    "Jan '25",
    "Feb '25",
    "Mar '25",
    "Apr '25",
    "May '25",
    "Jun '25",
    "Jul '25",
    "Aug '25",
    "Sep '25",
    "Oct '25",
    "Nov '25",
    "Dec '25",
    "Jan '26",
    "Feb '26",
    "Mar '26",
  ],
  raised: [
    42000, 38000, 51000, 44000, 47000, 62000, 55000, 49000, 58000, 71000, 83000,
    95000, 68000, 74000, 81000,
  ],
  donorCount: [
    610, 570, 740, 640, 690, 900, 800, 710, 840, 1020, 1190, 1370, 980, 1070,
    1170,
  ],
};

// ─── Issue Salience — SC-1 ────────────────────────────────────────────────
export const ISSUE_SALIENCE = [
  { label: "Immigration / Border", pct: 24, trend: "up" },
  { label: "Economy / Inflation", pct: 23, trend: "flat" },
  { label: "Healthcare", pct: 13, trend: "flat" },
  { label: "Crime / Public Safety", pct: 11, trend: "up" },
  { label: "Government Spending", pct: 9, trend: "up" },
  { label: "Education", pct: 8, trend: "down" },
  { label: "Environment / Energy", pct: 6, trend: "down" },
  { label: "Other", pct: 6, trend: "flat" },
];

// ─── Fundraising Drivers ──────────────────────────────────────────────────
export const FUNDRAISING_DRIVERS = [
  {
    label: "Primary Competitiveness",
    signal: "Strong Tailwind",
    r: 0.81,
    interpretation:
      "Mace crossed into the lead in Jan '26 — a live primary with a credible path to victory is the single strongest driver of small-dollar urgency. Competitive optics maximize response rates.",
  },
  {
    label: "McMaster $ Disadvantage",
    signal: "Headwind",
    r: 0.76,
    interpretation:
      "McMaster's $6.3M CoH vs. Mace's $1.5M — a 4:1 war-chest gap — is the most effective threat frame available. Donor urgency emails citing this disparity are proven performers.",
  },
  {
    label: "Kalshi Wave Signal",
    signal: "Tailwind",
    r: 0.68,
    interpretation:
      "Markets pricing an anti-establishment wave (D+86% House) create a reform-outsider narrative that resonates with Mace's challenger brand against a 2-term incumbent governor.",
  },
  {
    label: "Presidential Approval",
    signal: "Neutral",
    r: 0.45,
    interpretation:
      "Trump approval at 44% statewide suppresses base energy slightly. Messaging that ties Mace to Trump's core agenda (border, spending) offsets approval drag.",
  },
  {
    label: "Generic Ballot (D–R)",
    signal: "Headwind",
    r: -0.42,
    interpretation:
      "D+2 generic ballot indicates a challenging national environment for Rs in November. Urgency framing around primary importance elevates June as the real decisive election.",
  },
  {
    label: "Donation Velocity Momentum",
    signal: "Strong Tailwind",
    r: 0.81,
    interpretation:
      "Month-over-month WinRed acceleration is the strongest leading indicator of continued donor engagement. Sustaining velocity through primary is critical to general preparedness.",
  },
  {
    label: "Recession Risk",
    signal: "Headwind",
    r: -0.38,
    interpretation:
      "23% recession probability reduces discretionary giving. Fiscal responsibility messaging reframes economic anxiety as a reason to donate — not a reason to hold back.",
  },
];

// ─── Historical Election Results — Mace US House SC-1 ────────────────────
// Context: these are House results; 2026 SC Governor is her first statewide race
export const ELECTION_HISTORY = [
  {
    year: 2018,
    mace: 53.0,
    opponent: 47.0,
    note: "First election, narrow win · SC-1 House",
  },
  {
    year: 2020,
    mace: 51.1,
    opponent: 48.9,
    note: "Survived tough D-wave cycle · SC-1 House",
  },
  {
    year: 2022,
    mace: 53.6,
    opponent: 46.4,
    note: "Primary + general wins · SC-1 House",
  },
  {
    year: 2024,
    mace: 58.1,
    opponent: 41.9,
    note: "Expanded margin, strong base · SC-1 House",
  },
];

// ─── Donor Cohorts (Sprint 2: derive from WinRed S3) ──────────────────────
export const DONOR_COHORTS = [
  {
    name: "High Propensity",
    count: 203,
    lastGiftDays: 18,
    avgGift: 127,
    priority: "High",
    channel: "Email",
    frame: "Legislative win",
  },
  {
    name: "Lapsed (90–180d)",
    count: 847,
    lastGiftDays: 112,
    avgGift: 52,
    priority: "High",
    channel: "Email",
    frame: "Opponent threat",
  },
  {
    name: "Lapsed (180–365d)",
    count: 512,
    lastGiftDays: 224,
    avgGift: 38,
    priority: "Medium",
    channel: "Email + SMS",
    frame: "Primary urgency",
  },
  {
    name: "New Donors (<90d)",
    count: 341,
    lastGiftDays: 44,
    avgGift: 35,
    priority: "Medium",
    channel: "Email",
    frame: "Welcome + engage",
  },
  {
    name: "Major Donors ($500+)",
    count: 67,
    lastGiftDays: 31,
    avgGift: 843,
    priority: "High",
    channel: "Phone + Email",
    frame: "Personal ask",
  },
  {
    name: "One-Time Donors",
    count: 1240,
    lastGiftDays: 310,
    avgGift: 29,
    priority: "Low",
    channel: "Email",
    frame: "Re-engagement",
  },
];
